import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, X, Check, CheckCheck, RefreshCw, AlertTriangle, Info, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TYPE_CONFIG = {
  critical: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-400/10' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  success: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
};

export default function NotificationsDropdown() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        axiosAuth().get('/notifications'),
        axiosAuth().get('/notifications/unread-count')
      ]);
      setNotifications(notifsRes.data);
      setUnreadCount(countRes.data.count);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [axiosAuth]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const generateNotifications = async () => {
    setGenerating(true);
    try {
      await axiosAuth().post('/notifications/generate');
      await fetchNotifications();
    } catch (e) { console.error(e); }
    finally { setGenerating(false); }
  };

  const markAsRead = async (id) => {
    try {
      await axiosAuth().put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try {
      await axiosAuth().put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { console.error(e); }
  };

  const deleteNotification = async (id) => {
    try {
      await axiosAuth().delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (e) { console.error(e); }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button onClick={() => setOpen(!open)} data-testid="notifications-btn" className="relative p-2 text-white/60 hover:text-gold transition-colors">
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full" data-testid="unread-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-[#0A0A0A] border border-white/10 shadow-2xl z-50" data-testid="notifications-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h4 className="text-sm font-semibold">Notificaciones</h4>
            <div className="flex items-center gap-2">
              <button onClick={generateNotifications} disabled={generating} className="p-1.5 text-white/40 hover:text-gold transition-colors" title="Generar alertas">
                <RefreshCw size={14} className={generating ? 'animate-spin' : ''} />
              </button>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="p-1.5 text-white/40 hover:text-gold transition-colors" title="Marcar todas como leidas">
                  <CheckCheck size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={24} className="mx-auto text-white/20 mb-2" />
                <p className="text-xs text-white/40">No hay notificaciones</p>
                <button onClick={generateNotifications} className="mt-3 text-xs text-gold hover:underline">
                  Generar alertas inteligentes
                </button>
              </div>
            ) : (
              notifications.map((notif) => {
                const config = TYPE_CONFIG[notif.type] || TYPE_CONFIG.info;
                const Icon = config.icon;
                return (
                  <div key={notif.id} className={`p-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${!notif.read ? 'bg-white/[0.02]' : ''}`}
                    onClick={() => handleNotificationClick(notif)} data-testid={`notif-${notif.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 ${config.bg} rounded`}>
                        <Icon size={14} className={config.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium truncate ${!notif.read ? 'text-white' : 'text-white/60'}`}>{notif.title}</p>
                          {!notif.read && <span className="w-2 h-2 bg-gold rounded-full flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-white/40 mt-0.5 line-clamp-2">{notif.message}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-white/30">{new Date(notif.created_at).toLocaleDateString()}</span>
                          {notif.link && <ExternalLink size={10} className="text-white/30" />}
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }} className="p-1 text-white/20 hover:text-red-400 transition-colors">
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="p-3 border-t border-white/10">
            <button onClick={() => { navigate('/alerts'); setOpen(false); }} className="w-full text-center text-xs text-gold hover:underline">
              Ver todas las alertas
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
